import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { IconButton, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TextComponent from 'components/Input/TextComponent';
import SelectComponent from 'components/Input/SelectComponent';

import { useRestApi } from 'context/RestApiContext';
import { MessageModal } from 'components/modal/MessageModal';
import { ToastMessage } from 'components/modal/ToastMessage';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// 顧客の地図情報モーダル
function ClientMapModal(props) {
  // 住所の位置に地図を移動する
  const handleMoveToAddress = async () => {
    if (address) {
      try {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            address
          )}&key=${apiKey}`
        );
        const data = await res.json();
        if (data.status === 'OK' && data.results.length > 0) {
          const loc = data.results[0].geometry.location;
          setLatlng(`${loc.lat},${loc.lng}`);
          setToast({ type: 'info', message: '住所の位置に移動しました。' });
        } else {
          setToast({ type: 'error', message: '住所の座標が取得できませんでした。' });
        }
      } catch (e) {
        setToast({ type: 'error', message: '住所の座標取得でエラーが発生しました。' });
      }
    }
  };
  const restApi = useRestApi();

  const [show, setShow] = useState(false);
  // メッセージダイアログ
  const [dialog, setDialog] = useState({});

  // トースト表示
  const [toast, setToast] = useState({});

  // 顧客情報
  const [client, setClient] = useState({});

  // カード名
  const [title, setTitle] = useState('');
  // 住所
  const [address, setAddress] = useState('');
  // 座標
  const [latlng, setLatlng] = useState('');
  // zoom値（拡大率）
  const [zoom, setZoom] = useState(18);

  const mapRef = useRef(null);

  const handleClose = () => setShow(false);

  // Googleマップ共有URL生成
  const getMapShareUrl = () => {
    if (latlng) {
      return `https://www.google.com/maps/place/${latlng}`;
    } else if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    }
    return '';
  };

  // クリップボードにコピー
  const handleCopyMapUrl = () => {
    // Googleマップ「この場所を共有」と同じURL形式
    let mapUrlForCopy = '';
    if (latlng) {
      mapUrlForCopy = `https://www.google.com/maps/place/${latlng}`;
    } else if (address) {
      mapUrlForCopy = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`;
    }
    navigator.clipboard.writeText(mapUrlForCopy);
    setToast({ type: 'success', message: 'クリップボードにURLを保存しました。' });
  };

  // GoogleMap用のAPIキー（環境変数から取得）
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  useEffect(() => {
    if (props?.params?.show) {
      restApi.get(`/api/client/map/${props.params.client.CLIENT_CD}`, (data) => {
        setClient(props.params.client);
        setTitle(props.params.title);
        setAddress(data.map_data.BUILD_CITY);
        setLatlng(data.map_data.MAP_LATLNG);
        setZoom(data.map_data.MAP_ZOOM || 18);
        setShow(true);
      });
    }
  }, [props.params]);

  // GoogleMapの中心座標
  const getCenter = () => {
    if (latlng) {
      const [lat, lng] = latlng.split(',').map(Number);
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return { lat: 35.681236, lng: 139.767125 };
  };

  // 住所から座標自動取得（Geocoding API）
  useEffect(() => {
    if (!latlng && address) {
      const fetchLatLng = async () => {
        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
              address
            )}&key=${apiKey}`
          );
          const data = await res.json();
          if (data.status === 'OK' && data.results.length > 0) {
            const loc = data.results[0].geometry.location;
            setLatlng(`${loc.lat},${loc.lng}`);
          } else {
            // 住所が見つからない場合は兵庫県庁所在地（神戸市）
            setLatlng('34.690083,135.195511');
          }
        } catch (e) {
          // エラー時も兵庫県庁所在地（神戸市）
          setLatlng('34.690083,135.195511');
        }
      };
      fetchLatLng();
    }
  }, [address, latlng, apiKey]);

  // Google Maps JS API ローダー（日本語指定）
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    language: 'ja',
  });

  // 保存ボタンをクリック
  const handleClickSave = () => {
    restApi.post(
      `/api/client/map/save`,
      {
        map_data: {
          CLIENT_CD: client.CLIENT_CD,
          MAP_LATLNG: latlng,
          MAP_ZOOM: zoom,
        },
      },
      (_) => {
        setToast({ type: 'info', message: '保存しました。' });
      }
    );
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '1080px',
              backgroundColor: '#f1f2f4',
              marginTop: '40px',
            },
          },
        }}
        open={show}
        disableEnforceFocus={true} // フォーカス制御を無効にする
      >
        <DialogTitle>
          {title}　地図情報
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Row>
            <Col className="pr-1" md="12">
              <label className="label-text">住所</label>
              <div>
                〒{client.BUILD_POST_CODE}&nbsp;{client.BUILD_CITY}
                <Button
                  variant="outline-primary"
                  size="sm"
                  style={{ marginLeft: '12px', verticalAlign: 'middle' }}
                  onClick={handleMoveToAddress}
                >
                  住所の位置に移動
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="mt-1 pr-1" md="12">
              {!latlng && <div style={{ color: 'red' }}>※住所データが未登録です</div>}
              <div
                style={{
                  width: '100%',
                  height: '500px',
                  border: '2px solid #888',
                  borderRadius: '4px',
                }}
              >
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={getCenter()}
                    zoom={zoom}
                    onClick={(e) => {
                      const lat = e.latLng.lat();
                      const lng = e.latLng.lng();
                      setLatlng(`${lat},${lng}`);
                    }}
                    onLoad={(map) => {
                      mapRef.current = map;
                    }}
                    onZoomChanged={() => {
                      if (mapRef.current) setZoom(mapRef.current.getZoom());
                    }}
                  >
                    {latlng && <Marker position={getCenter()} />}
                  </GoogleMap>
                ) : (
                  <div>Loading Map...</div>
                )}
              </div>
              {/* Googleマップ共有URL表示 */}
              <div style={{ marginTop: '12px', wordBreak: 'break-all' }}>
                <label className="label-text">Googleマップ共有URL</label>
                <div>
                  <a href={getMapShareUrl()} target="_blank" rel="noopener noreferrer">
                    {getMapShareUrl()}
                  </a>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    style={{ marginLeft: '8px', verticalAlign: 'middle' }}
                    onClick={handleCopyMapUrl}
                  >
                    <ContentCopyIcon style={{ fontSize: 18, verticalAlign: 'middle' }} />
                    コピー
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </DialogContent>
        <DialogActions className="p-3" style={{ justifyContent: 'space-between' }}>
          <Button variant="secondary" className="footer-button" onClick={handleClose}>
            閉じる
          </Button>
          <Button
            variant="primary"
            className="btn-fill footer-button ml-4 mr-2"
            onClick={handleClickSave}
            style={{ flexGrow: 1 }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
      <MessageModal dialog={dialog}></MessageModal>
      {/* トーストメッセージ */}
      <ToastMessage toast={toast}></ToastMessage>
    </>
  );
}
export default ClientMapModal;
